import React, { useMemo, useState } from "react";
import { SHOP_IMAGE_BASE } from "./shopApi";
import "../index.css";

function formatPrice(value) {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function isVideoUrl(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

function openShareWindow(url) {
  window.open(url, "_blank", "noopener,noreferrer,width=680,height=760");
}

export default function ProductShareModal({ product, onClose }) {
  const [copied, setCopied] = useState("");

  const shareUrl = useMemo(() => {
    if (!product?.id) {
      return "https://shop.asherjewels.com.ph/";
    }
    return `https://shop.asherjewels.com.ph/product/${product.id}`;
  }, [product]);

  const shareTitle = useMemo(() => product?.name || "Asher Jewels PH", [product]);
  const shareDescription = useMemo(
    () => product?.description || "Discover this piece from Asher Jewels PH.",
    [product]
  );

  const shareText = useMemo(
    () => `${shareTitle} - PHP ${formatPrice(product?.pricePHP || 0)}\n${shareDescription}\n${shareUrl}`,
    [shareTitle, product, shareDescription, shareUrl]
  );

  const mediaUrl = useMemo(() => {
    const raw = product?.imageUrl || product?.images?.[0] || "";
    return raw ? `${SHOP_IMAGE_BASE}${raw}` : "";
  }, [product]);

  if (!product) {
    return null;
  }

  const handleCopy = async (mode = "link") => {
    const value = mode === "text" ? shareText : shareUrl;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(mode === "text" ? "Copied details for sharing." : "Product link copied.");
      window.setTimeout(() => setCopied(""), 2200);
    } catch {
      setCopied("Copy is not available on this device.");
      window.setTimeout(() => setCopied(""), 2200);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopy("text");
      return;
    }

    try {
      await navigator.share({
        title: shareTitle,
        text: shareDescription,
        url: shareUrl,
      });
    } catch {
      // User canceled or share target unavailable.
    }
  };

  const shareTargets = [
    {
      key: "facebook",
      label: "Facebook",
      action: () => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: "messenger",
      label: "Messenger",
      action: () => openShareWindow(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=966242223397117&redirect_uri=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      action: () => openShareWindow(`https://wa.me/?text=${encodeURIComponent(shareText)}`),
    },
    {
      key: "telegram",
      label: "Telegram",
      action: () => openShareWindow(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`${shareTitle} - PHP ${formatPrice(product?.pricePHP || 0)}`)}`),
    },
    {
      key: "x",
      label: "X",
      action: () => openShareWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareTitle} - PHP ${formatPrice(product?.pricePHP || 0)}`)}&url=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: "email",
      label: "Email",
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText)}`;
      },
    },
    {
      key: "tiktok",
      label: "TikTok",
      action: () => handleCopy("text"),
      helper: "Copies product details for TikTok sharing",
    },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <div className="share-modal__header">
          <p className="category-section__eyebrow">Share Product</p>
          <h2>{shareTitle}</h2>
        </div>

        <div className="share-modal__product">
          <div className="share-modal__media">
            {mediaUrl ? (
              isVideoUrl(mediaUrl) ? (
                <video src={mediaUrl} muted playsInline preload="metadata" />
              ) : (
                <img src={mediaUrl} alt={shareTitle} />
              )
            ) : null}
          </div>
          <div className="share-modal__details">
            <p className="share-modal__price">PHP {formatPrice(product?.pricePHP || 0)}</p>
            <p className="share-modal__description">{shareDescription}</p>
            <a href={shareUrl} target="_blank" rel="noreferrer" className="share-modal__link">
              {shareUrl}
            </a>
          </div>
        </div>

        <div className="share-modal__actions">
          <button type="button" className="share-pill share-pill--primary" onClick={handleNativeShare}>
            Share Now
          </button>
          <button type="button" className="share-pill" onClick={() => handleCopy("link")}>
            Copy Link
          </button>
        </div>

        {copied && <div className="share-modal__notice">{copied}</div>}

        <div className="share-modal__platforms">
          {shareTargets.map((target) => (
            <button
              key={target.key}
              type="button"
              className="share-target"
              onClick={target.action}
              title={target.helper || `Share to ${target.label}`}
            >
              <span className={`share-target__icon share-target__icon--${target.key}`} aria-hidden="true">
                {target.label.charAt(0)}
              </span>
              <span>{target.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
